import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpUploadProgressEvent
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';

import { EMPTY, merge, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import {
  LivSuccessResponse,
  PageableResponse
} from '@app/core/models/response-protocol.model';

import { environment } from '@env/environment';

export interface IRequestOptions {
  headers?: HttpHeaders;
  observe?: 'body';
  params?: HttpParams;
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  body?: any | null;
}

interface HttpServiceOptions {
  basePath?: string;
}

interface IRequestUploadOptions extends IRequestOptions {
  method?: 'post' | 'put' | 'patch';
}

export const HTTP_RESOURCE_CONFIG = new InjectionToken<HttpServiceOptions>(
  'HTTP_RESOURCE_CONFIG',
  {
    factory: () => ({ basePath: '' })
  }
);

@Injectable({
  providedIn: 'root'
})
export class HttpResource {
  constructor(
    protected httpClient: HttpClient,
    @Inject(HTTP_RESOURCE_CONFIG) protected config: { basePath: string }
  ) {}

  private get service(): string {
    const url = environment.apiUrl.endsWith('/')
      ? environment.apiUrl.substring(0, environment.apiUrl.length - 1)
      : environment.apiUrl;

    return url;
  }

  private get baseUrl(): string {
    return this.service + (this.config.basePath ?? '');
  }

  get<T>(endpoint: string, options: IRequestOptions = {}): Observable<T> {
    const url = encodeURI(this.baseUrl + endpoint);
    return this.httpClient
      .get<LivSuccessResponse<T>>(url, options)
      .pipe(map((res) => res.data));
  }

  getMany<T>(
    endpoint: string,
    options: IRequestOptions = {}
  ): Observable<PageableResponse<T>> {
    const url = encodeURI(this.baseUrl + endpoint);
    return this.httpClient.get<LivSuccessResponse<T>>(url, options).pipe(
      map((res) => ({
        data: res.data,
        meta: res.meta ?? {
          page: 1,
          pageSize: 10,
          pageCount: 0,
          totalCount: 0
        }
      }))
    );
  }

  upload<T>(
    endpoint: string,
    data: FormData,
    options: IRequestUploadOptions = {}
  ): Observable<HttpEvent<T>> {
    const timeout = 30 * 60 * 1000; // 30 minutes
    const headers = new HttpHeaders().append('timeout', String(timeout));

    const { method = 'post', ...externalOptions } = options;

    const _options: IRequestOptions = {
      headers,
      ...externalOptions
    };

    const url = encodeURI(this.baseUrl + endpoint);
    return this.httpClient.request<T>(method, url, {
      ..._options,
      reportProgress: true,
      observe: 'events',
      body: data
    });
  }

  private uploadFileChunkCreateForm(
    chunk: Blob,
    totalChunks: number,
    fileName: string,
    chunkIndex: number = 0,
    uploadId: string
  ): FormData {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('chunk_index', chunkIndex.toString());
    formData.append('total_chunks', totalChunks.toString());
    formData.append('file_name', fileName);
    formData.append('upload_id', uploadId);

    return formData;
  }

  /* eslint-disable max-lines-per-function */
  private uploadFileInChunksRequests<T>(
    totalChunks: number,
    chunkSize: number,
    fileData: File | null,
    url: string,
    progressSubject: Subject<HttpEvent<T>>,
    requestOptions: IRequestOptions
  ): Subject<HttpEvent<T>> {
    const uploadId = uuidv4();
    let completedChunks: number = 0;
    let completedBytes: number = 0;

    const chunkRequests = Array.from({ length: totalChunks }, (_, i) => {
      const chunk = fileData.slice(i * chunkSize, (i + 1) * chunkSize);

      const formData = this.uploadFileChunkCreateForm(
        chunk,
        totalChunks,
        fileData.name,
        i,
        uploadId
      );

      return this.httpClient
        .post<T>(url, formData, {
          ...requestOptions,
          reportProgress: true,
          observe: 'events'
        })
        .pipe(
          tap((event) => {
            if (event.type === HttpEventType.UploadProgress) {
              completedBytes += event.loaded;

              progressSubject.next({
                type: HttpEventType.UploadProgress,
                loaded: completedBytes,
                total: fileData.size
              } as HttpUploadProgressEvent);

              // Quando todos os chunks forem concluídos, emitir o final
              if (completedBytes === fileData.size) {
                progressSubject.next({
                  loaded: completedBytes,
                  total: fileData.size
                } as HttpUploadProgressEvent);

                progressSubject.complete();
              }
            }

            if (event.type === HttpEventType.Response) {
              completedChunks++;

              if (completedChunks === totalChunks) {
                const combined = Object.assign(
                  {},
                  (Array(totalChunks).fill(null)[i] = (
                    event as HttpResponse<T>
                  ).body)
                );

                progressSubject.next({
                  type: HttpEventType.Response,
                  body: combined as T
                } as HttpEvent<T>);
              }
            }
          }),
          catchError((error) => {
            progressSubject.error(error);

            return EMPTY;
          })
        );
    });

    merge(...chunkRequests, totalChunks).subscribe();

    return progressSubject;
  }
  /* eslint-enable max-lines-per-function */

  uploadFileInChunks<T>(
    endpoint: string,
    data: FormData,
    options: IRequestUploadOptions = {}
  ): Observable<HttpEvent<T>> {
    const fileData: File | null = data.get('file') as File | null;

    if (!fileData) {
      return throwError(() => new Error('Nenhum arquivo fornecido'));
    }

    const chunkSize = 16 * 1024 * 1024; // 16MB
    const totalChunks = Math.ceil(fileData.size / chunkSize);

    const timeout = 30 * 60 * 1000;
    const headers = new HttpHeaders().append('timeout', String(timeout));
    const _options: IRequestOptions = {
      headers,
      ...options
    };

    const url = encodeURI(this.baseUrl + endpoint);
    const progressSubject = new Subject<HttpEvent<T>>();

    this.uploadFileInChunksRequests<T>(
      totalChunks,
      chunkSize,
      fileData,
      url,
      progressSubject,
      _options
    );

    return progressSubject.asObservable();
  }

  download<T = File>(
    endpoint: string,
    options: IRequestOptions = {}
  ): Observable<T> {
    const timeout = 15 * 60 * 1000; // 15 minutes;
    const headers = new HttpHeaders().append('timeout', String(timeout));

    const _options: IRequestOptions = {
      headers,
      ...options
    };

    const url = encodeURI(this.baseUrl + endpoint);
    return this.httpClient.get<T>(url, {
      ..._options,
      responseType: 'blob' as 'json'
    });
  }

  post<T>(
    endpoint: string,
    body: unknown,
    options: IRequestOptions = {}
  ): Observable<T> {
    const url = encodeURI(this.baseUrl + endpoint);
    return this.httpClient
      .post<LivSuccessResponse<T>>(url, body, options)
      .pipe(map((res) => res.data));
  }

  put<T>(
    endpoint: string,
    body: unknown,
    options: IRequestOptions = {}
  ): Observable<T> {
    const url = encodeURI(this.baseUrl + endpoint);
    return this.httpClient
      .put<LivSuccessResponse<T>>(url, body, options)
      .pipe(map((res) => res.data));
  }

  patch<T>(
    endpoint: string,
    body: unknown,
    options: IRequestOptions = {}
  ): Observable<T> {
    const url = encodeURI(this.baseUrl + endpoint);
    return this.httpClient
      .patch<LivSuccessResponse<T>>(url, body, options)
      .pipe(map((res) => res.data));
  }

  delete<T>(endpoint: string, options: IRequestOptions = {}): Observable<T> {
    const url = encodeURI(this.baseUrl + endpoint);
    return this.httpClient
      .delete<LivSuccessResponse<T>>(url, options)
      .pipe(map((res) => res.data));
  }

  graphql<T>(query: string, options: IRequestOptions = {}): Observable<T> {
    const url = encodeURI(this.service + '/graphql');
    return this.httpClient
      .post<LivSuccessResponse<T>>(url, JSON.stringify({ query }), options)
      .pipe(map((res) => res.data));
  }
}
